<!--
  @component FleetMapBlock — the Deployments command center's centerpiece.
  tier:   deployments block (the "wow")

  A geo map of your fleet: every app plotted at its deploy region, clustered,
  status-coloured, pulsing while a deploy is in flight. v1 is a stylised
  equirectangular canvas (graticule + glowing geo-nodes); the data is the real
  per-project rollup over the live deploy-event stream.

  Two-layer by design — `deployment points` (built here) + an `activity/heat`
  layer (v2). The v2 globe replaces the <svg> internals; the projection +
  point/cluster shapes (fleet-util) stay, so the swap is contained.
-->
<script lang="ts">
  import { useIdentityIO } from "../../accounts/IdentityIO.svelte";
  import { useProvenanceIO } from "../../../../global/io/nodes/deployment/ProvenanceIO.svelte";
  import { projectRollup, type ProjectStatus } from "../../home/home-util.js";
  import {
    fleetPoints,
    clusterByRegion,
    project,
    scopeProjects,
    scopeEvents,
    type RegionCluster,
  } from "./fleet-util.js";
  import { useScopeIO } from "./ScopeIO.svelte";

  const identity = useIdentityIO();
  const provenance = useProvenanceIO();
  const scope = useScopeIO();

  const scopedProjects = $derived(scopeProjects(identity.projects, scope.projectIds));
  const scopedEvents = $derived(scopeEvents(provenance.events, scope.projectIds));

  const W = 1000;
  const H = 500;

  const clusters = $derived.by<RegionCluster[]>(() => {
    const roll = projectRollup(scopedEvents);
    return clusterByRegion(fleetPoints(scopedProjects, roll));
  });
  const total = $derived(clusters.reduce((n, c) => n + c.points.length, 0));
  const anyBuilding = $derived(clusters.some((c) => c.status === "building"));

  // Graticule lines (a clean world-projection feel without a continent asset).
  const meridians = [-150, -120, -90, -60, -30, 0, 30, 60, 90, 120, 150];
  const parallels = [-60, -30, 0, 30, 60];

  function sx(lng: number): number { return project(0, lng).x * W; }
  function sy(lat: number): number { return project(lat, 0).y * H; }

  function center(c: RegionCluster): { x: number; y: number } {
    const p = project(c.region.lat, c.region.lng);
    return { x: p.x * W, y: p.y * H };
  }
  /** Satellite dot position around a hub (ring of app dots). */
  function satellite(cx: number, cy: number, i: number, n: number): { x: number; y: number } {
    if (n <= 1) return { x: cx, y: cy };
    const a = (i / n) * Math.PI * 2 - Math.PI / 2;
    const r = 15;
    return { x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r };
  }

  const STATUS_FILL: Record<ProjectStatus, string> = {
    live: "var(--success)",
    building: "var(--warning)",
    error: "var(--error)",
    idle: "var(--text-tertiary)",
  };
</script>

<section class="map-block">
  <header class="head">
    <div class="title-wrap">
      <h2>Fleet map</h2>
      <p class="sub">Where your apps run · live across regions</p>
    </div>
    <span class="region-chip">{clusters.length} region{clusters.length === 1 ? "" : "s"} · {total} app{total === 1 ? "" : "s"}</span>
  </header>

  <div class="canvas-wrap">
    <svg viewBox="0 0 {W} {H}" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Fleet map">
      <defs>
        <radialGradient id="oceanGlow" cx="50%" cy="42%" r="75%">
          <stop offset="0%" stop-color="rgba(120,150,200,0.10)" />
          <stop offset="100%" stop-color="rgba(0,0,0,0)" />
        </radialGradient>
      </defs>

      <rect x="0" y="0" width={W} height={H} fill="url(#oceanGlow)" />

      <!-- Graticule -->
      <g class="grid">
        {#each meridians as lng (lng)}
          <line x1={sx(lng)} y1="0" x2={sx(lng)} y2={H} class:axis={lng === 0} />
        {/each}
        {#each parallels as lat (lat)}
          <line x1="0" y1={sy(lat)} x2={W} y2={sy(lat)} class:axis={lat === 0} />
        {/each}
      </g>

      <!-- Region clusters -->
      {#each clusters as c (c.region.code)}
        {@const ctr = center(c)}
        <g class="cluster" class:pulsing={c.status === "building"}>
          <circle class="glow" cx={ctr.x} cy={ctr.y} r="34" fill={STATUS_FILL[c.status]} />
          {#each c.points as p, i (p.id)}
            {@const s = satellite(ctr.x, ctr.y, i, c.points.length)}
            <circle class="sat" cx={s.x} cy={s.y} r="3" fill={STATUS_FILL[p.status]}>
              <title>{p.name} · {p.status}</title>
            </circle>
          {/each}
          <circle class="hub" cx={ctr.x} cy={ctr.y} r="6" fill={STATUS_FILL[c.status]} />
          <text class="region-label" x={ctr.x} y={ctr.y + 30} text-anchor="middle">
            {c.region.label} · {c.region.code}
          </text>
        </g>
      {/each}
    </svg>

    {#if total === 0}
      <div class="empty">No deployments yet — ship a project and it lands on the map.</div>
    {/if}

    <!-- Legend + the v2 affordance -->
    <div class="legend">
      <span><i style="background: var(--success)"></i>Live</span>
      <span><i style="background: var(--warning)"></i>Building</span>
      <span><i style="background: var(--error)"></i>Issue</span>
    </div>
    <span class="v2-chip" title="Audience / IP heatmap + globe — coming in v2">Audience heat · v2</span>
  </div>
</section>

<style>
  .map-block {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    padding: var(--space-5);
    background: var(--glass-fill);
    border: 0.5px solid var(--border);
    border-radius: var(--radius-xl, 18px);
    backdrop-filter: var(--glass-blur);
    -webkit-backdrop-filter: var(--glass-blur);
    font-family: var(--type-font);
  }
  .head { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--space-3); }
  h2 { margin: 0; font-size: 15px; font-weight: 600; letter-spacing: -0.01em; color: var(--text-primary); }
  .sub { margin: 2px 0 0; font-size: 12px; color: var(--text-tertiary); }
  .region-chip {
    font-size: 11px; font-weight: 500; white-space: nowrap;
    color: var(--text-secondary);
    background: var(--bg-hover); border: 0.5px solid var(--border-subtle);
    border-radius: var(--radius-pill); padding: 4px 10px;
  }

  .canvas-wrap {
    position: relative;
    border-radius: var(--radius-lg, 14px);
    overflow: hidden;
    background:
      radial-gradient(120% 90% at 50% 0%, color-mix(in oklab, var(--accent, #5b8cff) 8%, var(--surface-sunken)) 0%, var(--surface-sunken) 60%);
    border: 0.5px solid var(--border-subtle);
  }
  svg { display: block; width: 100%; height: auto; }

  .grid line { stroke: var(--border); stroke-width: 0.5; opacity: 0.35; }
  .grid line.axis { opacity: 0.6; }

  .glow { opacity: 0.18; filter: blur(2px); }
  .hub { stroke: var(--surface-base); stroke-width: 1.5; }
  .sat { opacity: 0.9; }
  .region-label {
    fill: var(--text-secondary); font-size: 12px; font-family: var(--type-font);
    font-weight: 500; letter-spacing: 0.01em;
  }
  .cluster.pulsing .glow { animation: pulse 1.8s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
  .cluster.pulsing .hub  { animation: pulse 1.8s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
  @keyframes pulse { 0%,100% { opacity: 0.18; } 50% { opacity: 0.4; } }
  @media (prefers-reduced-motion: reduce) {
    .cluster.pulsing .glow, .cluster.pulsing .hub { animation: none; }
  }

  .empty {
    position: absolute; inset: 0; display: grid; place-items: center;
    text-align: center; padding: var(--space-6);
    font-size: 13px; color: var(--text-tertiary);
  }

  .legend {
    position: absolute; left: var(--space-4); bottom: var(--space-3);
    display: flex; gap: var(--space-3);
    font-size: 11px; color: var(--text-tertiary);
  }
  .legend span { display: inline-flex; align-items: center; gap: 5px; }
  .legend i { width: 7px; height: 7px; border-radius: 50%; display: inline-block; }
  .v2-chip {
    position: absolute; right: var(--space-4); bottom: var(--space-3);
    font-size: 10px; font-weight: 500; letter-spacing: 0.04em; text-transform: uppercase;
    color: var(--text-ghost);
    border: 0.5px dashed var(--border); border-radius: var(--radius-pill);
    padding: 3px 8px;
  }
</style>
